import { useState, useEffect, useMemo } from 'react';
import { Button, message } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { Loading, FinalizateBar, FinalizateButton } from 'src/DigitalProtocolModule/components';
import { AnalysisSet } from 'src/DigitalProtocolModule/components/AnalysisSet';
import { useSelector } from 'react-redux';
import { fetchHistory } from 'src/actions/historyTrial';
import {
	getStudyPopulationList,
	saveStudyPopulation,
	updateStudyPopulation
} from 'src/DigitalProtocolModule/utils/ajax-proxy';
import styles from './index.module.scss';


export default function StudyPopulation() {

  const trial_uuid = localStorage.getItem('trial-id');

	const finalizeStatus = useSelector(fetchHistory).data?.dpFinalizeStatus;
	const isLocked = useMemo(
		() => Boolean(finalizeStatus?.study_population_finalize_flag),
		[finalizeStatus]
	);

    const [populationDescription, setPopulationDescription] = useState('');
    const [stratificationCriteria, setStratificationCriteria] = useState('');
		const [isLoading, setIsLoading] = useState(false);
		const [finalAnalysisSet, setFinalAnalysisSet] = useState([]);
		const [populationList ,setPopulationList] = useState<Digital_Protocol_StudyPopulation.PopulationList[]>([]);

		// Function to handle changes in the analysis set from the AnalysisSet component
		const handleAnalysisSetChange = (newAnalysisSet: Digital_Protocol_StudyPopulation.NewAnalysisSet[]) => {
			setFinalAnalysisSet(newAnalysisSet);
		};

		const handleSave = async () => {
			if (!populationDescription) {
				message.error('Population Description is required');
				return;
			}	
			if (finalAnalysisSet.some(item => !item.analysis_set_name.trim())) {
				message.error('Analysis Name is required');
				return;
			}		
			if(populationList[0]?.study_population_id){
				const payload = {
					study_population_id: populationList[0]?.study_population_id,					
					population_description: populationDescription,
					analysis_set: finalAnalysisSet,
					straitification_criteria: stratificationCriteria,
				};
				try {
					setIsLoading(true);
					await updateStudyPopulation(payload);
					message.success('Study population updated successfully!');
					getStudyPopulation();
				} catch (error) {
					setIsLoading(false);
					console.error('Error saving study population:', error);
				}finally{
					setIsLoading(false);
				}
			}else{
				const payload = {
					trial_uuid,
					population_description: populationDescription,
					analysis_set: finalAnalysisSet,
					straitification_criteria: stratificationCriteria,
				};
				try {
					setIsLoading(true);
					await saveStudyPopulation(payload);
					message.success('Study population saved successfully!');
					getStudyPopulation();
				} catch (error) {
					setIsLoading(false);
					console.error('Error saving study population:', error);
				}finally{
					setIsLoading(false);
				}
			}
		};

		const getStudyPopulation = async () => {
			try {
				setIsLoading(true);
				const res: Digital_Protocol_StudyPopulation.PopulationList = await getStudyPopulationList(trial_uuid);				
				if (res) {
					setPopulationDescription(res.population_description || '');
					setStratificationCriteria(res.stratification_criteria || '');
					setFinalAnalysisSet(Array.isArray(res.analysis_set) ? res.analysis_set : []);
				}				
				setPopulationList([res]);				
			} catch (error) {
				setIsLoading(false);
				console.error('Error saving study population:', error);
				message.error('Failed to retrieve study population.');
			}finally{
				setIsLoading(false);
			}
		};

		useEffect(()=>{
			getStudyPopulation();
		}, []);

    return (
      <>
	  	{isLocked && (
        	<div className={styles.bar}>
          		<FinalizateBar category="population" />
			</div>
      	)}
				<div className={styles.container}>
          <div className={styles.header}>
            <span className={styles.title}>Study Population</span>
            <div className={styles.btns}>
				{!isLocked && <FinalizateButton category="population" />}
            </div>
          </div>          
					<div>
							<label className={styles.subTitle}>Population Description <span className={styles.required}>*</span></label>
							<TextArea
								className={styles.descriptionBox}
								placeholder="Describe your Population" 
								value={populationDescription}
								onChange={(e) => setPopulationDescription(e.target.value)}
								maxLength={500}
								readOnly={isLocked}
							/>
					</div>
					<div>
							<label className={styles.subTitle}>Choose Analysis Sets</label>
							<Loading isLoading={isLoading}>															
									<AnalysisSet
										trial_uuid={trial_uuid}
										populationList={populationList}										
										onAnalysisSetChange={handleAnalysisSetChange}
										isReadOnly={isLocked}
									/>								
							</Loading>
					</div>
					<div>
							<label className={styles.subTitle}>Stratification Criteria</label>
							<TextArea
								className={styles.descriptionBox}
								placeholder="Describe Your Stratification Criteria" 
								value={stratificationCriteria}
								onChange={(e) => setStratificationCriteria(e.target.value)}
								maxLength={500}
								readOnly={isLocked}
							/>
					</div>  
						<div className={styles.btns}>
              <Button
                className={styles.primaryBtn}
                key="save"
                type="text"
				disabled={isLocked}
								onClick={handleSave}
              >
                Save
              </Button>
            </div>   
				</div>     
    	</>
  	);
};